import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import JoditEditor from "jodit-react";
import NumberField from "../../../Components/NumberField";
import Handler from "../../../Helpers/Handler";
import { firstError, htmlTextLength, invalidClass, numberError, RULES, textError } from "../../../Helpers/bookingRules";

const emptyForm = () => ({
    tpId: null,
    name: "",
    percentage: "",
    description: "",
    amount: "",
});

const JODIT_CONFIG = {
    readonly: false,
    height: 260,
    placeholder: "Describe this trip protection plan...",
    toolbarAdaptive: false,
    buttons: [
        "bold", "italic", "underline", "|",
        "ul", "ol", "|",
        "align", "|",
        "link", "|",
        "undo", "redo",
    ],
};

const PlanFormModal = ({ plan, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const editorRef = useRef(null);
    const mode = plan?.tpId ? "edit" : "create";
    const [form, setForm] = useState(plan ? {
        tpId: plan.tpId,
        name: plan.name || "",
        percentage: plan.percentage ?? "",
        description: plan.description || "",
        amount: plan.amount ?? "",
    } : emptyForm());
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const setField = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setErrors((e) => ({ ...e, [key]: null })); };

    const joditConfig = useMemo(() => JODIT_CONFIG, []);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => { el.removeEventListener("hidden.bs.modal", handleHidden); instance.hide(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async () => {
        // Same limits as the API (BookingRules.js).
        const descLength = htmlTextLength(form.description);
        const next = {
            name: textError(form.name, { label: "Name", required: true, min: RULES.PLAN_NAME_MIN, max: RULES.PLAN_NAME_MAX, pattern: RULES.PLAN_NAME_RE, patternMessage: "Name can only use letters, numbers, spaces and & ( ) + -" }),
            percentage: numberError(form.percentage, { label: "Percentage", required: true, min: 0, max: 100, maxDecimals: 2 }),
            amount: numberError(form.amount, { label: "Amount", required: true, min: 0, max: RULES.MONEY_MAX, maxDecimals: 2 }),
            description: !descLength ? "Description is required"
                : descLength < RULES.PLAN_DESC_TEXT_MIN ? `Description must be at least ${RULES.PLAN_DESC_TEXT_MIN} characters`
                : descLength > RULES.PLAN_DESC_TEXT_MAX ? `Description can be at most ${RULES.PLAN_DESC_TEXT_MAX} characters` : null,
        };
        setErrors(next);
        const problem = firstError(next);
        if (problem) { toast.error(problem); return; }
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            percentage: Number(form.percentage),
            description: form.description,
            amount: Number(form.amount),
        };
        const response = mode === "create"
            ? await Handler({ method: "post", url: "/admin/booking/protection", data: payload })
            : await Handler({ method: "patch", url: `/admin/booking/protection/${form.tpId}`, data: payload });
        setSaving(false);
        if (response?.success) {
            toast.success(response?.data?.message || "Saved successfully");
            onSaved?.();
        } else {
            toast.error(response?.data?.message || "Unable to save this plan");
        }
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{mode === "create" ? "New trip protection plan" : "Edit trip protection plan"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-2">
                            <div className="col-12">
                                <label className="form-label small text-muted mb-1">Name</label>
                                <input type="text" maxLength={RULES.PLAN_NAME_MAX} className={`form-control${invalidClass(errors.name)}`} value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Basic, Premium" aria-invalid={!!errors.name} />
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1">Percentage</label>
                                <NumberField decimals={2} min={0} max={100} className={`form-control${invalidClass(errors.percentage)}`} value={form.percentage} onChange={(v) => setField("percentage", v)} placeholder="e.g. 5" aria-invalid={!!errors.percentage} />
                                {errors.percentage && <div className="invalid-feedback">{errors.percentage}</div>}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1">Amount</label>
                                <NumberField decimals={2} min={0} max={RULES.MONEY_MAX} className={`form-control${invalidClass(errors.amount)}`} value={form.amount} onChange={(v) => setField("amount", v)} aria-invalid={!!errors.amount} />
                                {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                            </div>
                            <div className="col-12">
                                <label className="form-label small text-muted mb-1">Description</label>
                                <JoditEditor
                                    ref={editorRef}
                                    value={form.description}
                                    config={joditConfig}
                                    onBlur={(content) => setField("description", content)}
                                />
                                {errors.description && <div className="text-danger small mt-1" role="alert">{errors.description}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanFormModal;
